import { useMemo, useState } from "react"
import Modal from "react-modal"
import { GoArrowRight } from "react-icons/go"

import Button from "../../../component/button/button"
import LevelSelectors, {
  EMPTY_LEVEL_VALUES,
  LevelFieldNames,
  LevelFieldValues,
} from "../../Questions/components/LevelSelectors"
import {
  DEFAULT_LEVEL_LABELS,
  labelAt,
  normalizeLabels,
} from "../../Subjects/detail/hierarchyUtils"
import { buildHierarchyTopicFromLevels } from "../../../utils/hierarchyTopic"
import { HierarchyTopicSelection } from "../../../types/hierarchyTopic"

type HierarchyTopicModalProps = {
  subjectId: string
  levelLabels?: string[]
  isOpen: boolean
  onClose: () => void
  onConfirm: (topic: Omit<HierarchyTopicSelection, "questions">) => void
}

const HierarchyTopicModal = ({
  subjectId,
  levelLabels,
  isOpen,
  onClose,
  onConfirm,
}: HierarchyTopicModalProps) => {
  const labels = useMemo(
    () =>
      normalizeLabels(levelLabels?.length ? levelLabels : DEFAULT_LEVEL_LABELS),
    [levelLabels],
  )

  const [values, setValues] = useState<LevelFieldValues>(EMPTY_LEVEL_VALUES)
  const [names, setNames] = useState<LevelFieldNames>({})
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setValues(EMPTY_LEVEL_VALUES)
    setNames({})
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleConfirm = () => {
    const requiredLevels = Array.from({ length: 2 }, (_, idx) => idx + 1)

    const missing = requiredLevels.find(
      (level) => !values[`level_${level}` as keyof LevelFieldValues],
    )
    if (missing) {
      setError(`Please select ${labelAt(labels, missing).toLowerCase()}.`)
      return
    }

    onConfirm(buildHierarchyTopicFromLevels(values, names, labels))
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel='Select hierarchy path'
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          padding: 24,
          border: "none",
          borderRadius: 8,
          width: "min(520px, 92vw)",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow:
            "0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)",
        },
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Select hierarchy path
        </h2>
        <p className='text-sm text-gray-600'>
          Choose the topic path for this slot. The first two configured levels
          are required.
        </p>

        <div className='flex flex-col gap-3'>
          <LevelSelectors
            subjectId={subjectId}
            levelLabels={labels}
            values={values}
            names={names}
            onChange={(nextValues, nextNames) => {
              setValues(nextValues)
              setNames(nextNames)
              setError(null)
            }}
            className='template_hierarchy_field'
          />
        </div>

        {error && <p className='text-sm text-red-600'>{error}</p>}

        <div className='flex justify-end gap-3 pt-2'>
          <Button
            text='Cancel'
            type='link'
            onClick={handleClose}
            style={{ color: "var(--brand-red-700)", fontSize: 16 }}
          />
          <Button
            text='Add'
            type='secondary'
            icon={<GoArrowRight />}
            style={{ flexDirection: "row-reverse", fontSize: 16 }}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </Modal>
  )
}

export default HierarchyTopicModal
